import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = '/api';

async function fetchRetry(url, opts, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      return await axios.get(url, { ...opts, timeout: 10000 });
    } catch (err) {
      if (i < retries && (!err.response || err.response.status >= 500 || err.code === 'ECONNABORTED')) {
        await new Promise(r => setTimeout(r, 2000));
        continue;
      }
      throw err;
    }
  }
}

export default function useAdminData(token) {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [products, setProducts] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const getOpts = () => ({
    headers: { Authorization: `Bearer ${tokenRef.current}`, 'Content-Type': 'application/json' },
    withCredentials: true,
  });

  const loadStats = async () => {
    try {
      const { data } = await fetchRetry(`${API}/admin/stats`, getOpts());
      setStats(data.stats);
    } catch { /* */ }
  };

  const loadReports = async () => {
    try { const { data } = await fetchRetry(`${API}/admin/reports`, getOpts()); setReports(data.reports || []); } catch { /* */ }
  };
  const loadTransactions = async () => {
    try { const { data } = await fetchRetry(`${API}/admin/transactions`, getOpts()); setTransactions(data.transactions || []); } catch { /* */ }
  };
  const loadTasks = async () => {
    try { const { data } = await fetchRetry(`${API}/admin/tasks`, getOpts()); setTasks(data.tasks || []); } catch { /* */ }
  };
  const loadProducts = async () => {
    try { const { data } = await fetchRetry(`${API}/admin/affiliate-products`, getOpts()); setProducts(data.products || []); } catch { /* */ }
  };
  const loadInstallers = async () => {
    try { const { data } = await fetchRetry(`${API}/admin/installers`, getOpts()); setInstallers(data.installers || []); } catch { /* */ }
  };
  const loadCampaigns = async () => {
    try { const { data } = await fetchRetry(`${API}/admin/marketing`, getOpts()); setCampaigns(data.campaigns || []); } catch { /* */ }
  };

  const loadAll = async () => {
    setLoading(true);
    await loadStats();
    setLoading(false);
    // Background: load the rest without blocking render
    loadReports();
    loadTransactions();
    loadTasks();
    loadProducts();
    loadInstallers();
    loadCampaigns();
  };

  useEffect(() => {
    if (token) loadAll();
  }, [token]);

  // Action helpers using tokenRef for always-fresh auth
  const approveProduct = async (id) => { await axios.put(`${API}/admin/affiliate-products/${id}`, { action: 'approve' }, getOpts()); loadProducts(); loadStats(); };
  const rejectProduct = async (id, reason) => { await axios.put(`${API}/admin/affiliate-products/${id}`, { action: 'reject', reason }, getOpts()); loadProducts(); loadStats(); };
  const updateProductLink = async (id, link, status) => { await axios.put(`${API}/admin/affiliate-products/${id}/link`, { affiliate_link: link, link_status: status }, getOpts()); loadProducts(); };
  const approveInstaller = async (id) => { await axios.put(`${API}/admin/installers/${id}`, { action: 'approve' }, getOpts()); loadInstallers(); loadStats(); };
  const rejectInstaller = async (id) => { await axios.put(`${API}/admin/installers/${id}`, { action: 'reject' }, getOpts()); loadInstallers(); loadStats(); };
  const approveCampaign = async (id) => { await axios.put(`${API}/admin/marketing/${id}`, { action: 'approve' }, getOpts()); loadCampaigns(); };
  const rejectCampaign = async (id) => { await axios.put(`${API}/admin/marketing/${id}`, { action: 'reject' }, getOpts()); loadCampaigns(); };

  const triggerAffiliateResearch = async (cat, country = 'NL', lang = 'nl') => { const { data } = await axios.post(`${API}/admin/agents/affiliate/research`, { category: cat, country, language: lang }, getOpts()); loadTasks(); return data; };
  const triggerInstallerResearch = async (region, country = 'NL', spec = '', lang = 'nl') => { const { data } = await axios.post(`${API}/admin/agents/installers/research`, { region, country, specialization: spec, language: lang }, getOpts()); loadTasks(); return data; };
  const triggerReviewCheck = async (id) => { const { data } = await axios.post(`${API}/admin/agents/reviews/check/${id}`, {}, getOpts()); loadTasks(); return data; };
  const triggerBatchReviewCheck = async (cat, limit = 10) => { const { data } = await axios.post(`${API}/admin/agents/reviews/batch-check`, { category: cat || null, limit }, getOpts()); loadTasks(); return data; };
  const triggerMarketingGenerate = async (ch, angle, country = 'NL', lang = 'nl') => { const { data } = await axios.post(`${API}/admin/agents/marketing/generate`, { channel: ch, audience_angle: angle, country, language: lang }, getOpts()); loadTasks(); return data; };
  const refreshCampaign = async (id) => { const { data } = await axios.post(`${API}/admin/agents/marketing/refresh/${id}`, {}, getOpts()); loadTasks(); loadCampaigns(); return data; };
  const updateCampaignPerformance = async (id, perf) => { await axios.put(`${API}/admin/marketing/${id}/performance`, perf, getOpts()); loadCampaigns(); };
  const getAudienceAngles = async () => { const { data } = await axios.get(`${API}/admin/marketing/angles`, getOpts()); return data.angles || []; };
  const suggestLinkForProduct = async (id) => { const { data } = await axios.post(`${API}/admin/agents/affiliate/suggest/${id}`, {}, getOpts()); loadTasks(); return data; };
  const suggestLinksBatch = async (cat, limit = 10) => { const { data } = await axios.post(`${API}/admin/agents/affiliate/suggest-batch`, { category: cat || null, limit }, getOpts()); loadTasks(); return data; };
  const updateSuggestion = async (id, action, fields = {}) => { await axios.put(`${API}/admin/affiliate-products/${id}/suggestion`, { action, ...fields }, getOpts()); loadProducts(); };

  return {
    stats, reports, transactions, tasks, products, installers, campaigns, loading,
    reload: loadAll, reloadProducts: loadProducts, reloadInstallers: loadInstallers,
    reloadTasks: loadTasks, reloadCampaigns: loadCampaigns, reloadStats: loadStats,
    approveProduct, rejectProduct, updateProductLink,
    approveInstaller, rejectInstaller,
    approveCampaign, rejectCampaign,
    triggerAffiliateResearch, triggerInstallerResearch, triggerReviewCheck, triggerBatchReviewCheck, triggerMarketingGenerate,
    refreshCampaign, updateCampaignPerformance, getAudienceAngles,
    suggestLinkForProduct, suggestLinksBatch, updateSuggestion,
  };
}
